import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    entity: String, // e.g., "photo", "asset"
    selectedIds: Array
  }

  static targets = ["item", "selectAll", "counter"]

  connect() {
    // Save the current page pattern for persistence logic
    this.currentPagePattern = this.extractPagePattern(window.location.pathname)
    
    // Load persisted selection from sessionStorage
    this.loadPersistedSelection()
    this.updateCounter()
    this.updateSelectAllState()
    this.restoreCheckboxStates()
    
    // Use nextTick to ensure other controllers are connected before triggering events
    setTimeout(() => {
      if (this.hasSelection) {
        this.toggleFloatingMenu()
        
        // Also try to directly show the floating menu as a fallback
        const floatingMenu = this.element.querySelector('[data-floating-action-menu-target="menu"]')
        if (floatingMenu) {
          floatingMenu.classList.remove('floating-menu--hidden')
          floatingMenu.classList.add('floating-menu--visible')
          
          const counter = this.element.querySelector('[data-floating-action-menu-target="counter"]')
          if (counter) {
            counter.textContent = `${this.selectedIdsValue.length} selected`
          }
        }
      }
    }, 0)

    this.onFrameLoad = this.handleFrameLoad.bind(this)
    document.addEventListener("turbo:frame-load", this.onFrameLoad)
  }

  disconnect() {
    // Save selection to sessionStorage when controller disconnects
    this.persistSelection()
    document.removeEventListener("turbo:frame-load", this.onFrameLoad)
  }

  toggleSelection(event) {
    const checkbox = event.target
    const itemId = checkbox.dataset.itemId
    
    if (checkbox.checked) {
      if (!this.selectedIdsValue.includes(itemId)) {
        this.selectedIdsValue = [...this.selectedIdsValue, itemId]
      }
    } else {
      this.selectedIdsValue = this.selectedIdsValue.filter(id => id !== itemId)
    }
    
    this.updateCounter()
    this.updateSelectAllState()
    this.toggleFloatingMenu()
    this.persistSelection()
  }

  selectAll(event) {
    const selectAllCheckbox = event.target
    
    if (selectAllCheckbox.checked) {
      this.selectAllItems()
    } else {
      this.deselectAllItems()
    }
    
    this.updateCounter()
    this.toggleFloatingMenu()
    this.persistSelection()
  }

  // Select-all only affects the items on the current page; selections from
  // other pages (persisted in sessionStorage) must be preserved.
  selectAllItems() {
    const allCheckboxes = this.itemCheckboxes
    const pageIds = allCheckboxes.map(cb => cb.dataset.itemId)
    this.selectedIdsValue = [...new Set([...this.selectedIdsValue, ...pageIds])]
    allCheckboxes.forEach(cb => cb.checked = true)
  }

  deselectAllItems() {
    const allCheckboxes = this.itemCheckboxes
    const pageIds = new Set(allCheckboxes.map(cb => cb.dataset.itemId))
    this.selectedIdsValue = this.selectedIdsValue.filter(id => !pageIds.has(id))
    allCheckboxes.forEach(cb => cb.checked = false)
  }

  get itemCheckboxes() {
    return Array.from(this.element.querySelectorAll('input[type="checkbox"][data-item-id]'))
  }

  clearSelection() {
    this.selectedIdsValue = []
    const allCheckboxes = this.element.querySelectorAll('input[type="checkbox"][data-item-id]')
    allCheckboxes.forEach(cb => cb.checked = false)
    
    if (this.hasSelectAllTarget) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    }
    
    this.updateCounter()
    this.toggleFloatingMenu()
    this.clearPersistedSelection()
  }

  updateCounter() {
    const count = this.selectedIdsValue.length
    if (this.hasCounterTarget) {
      this.counterTarget.textContent = `${count} selected`
    }
  }

  updateSelectAllState() {
    if (!this.hasSelectAllTarget) return
    
    // Compare against the items on the current page only, since the selection
    // may also include items from other pages.
    const allCheckboxes = this.itemCheckboxes
    const totalCount = allCheckboxes.length
    const checkedCount = allCheckboxes.filter(cb => this.selectedIdsValue.includes(cb.dataset.itemId)).length
    
    if (checkedCount === 0 || totalCount === 0) {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = false
    } else if (checkedCount === totalCount) {
      this.selectAllTarget.checked = true
      this.selectAllTarget.indeterminate = false
    } else {
      this.selectAllTarget.checked = false
      this.selectAllTarget.indeterminate = true
    }
  }

  toggleFloatingMenu() {
    const hasSelection = this.selectedIdsValue.length > 0
    this.dispatch("selection-changed", { 
      detail: { 
        hasSelection,
        selectedIds: this.selectedIdsValue,
        count: this.selectedIdsValue.length,
        entity: this.entityValue
      } 
    })
  }

  // Getter for use by other controllers
  get selectedIds() {
    return this.selectedIdsValue
  }

  get hasSelection() {
    return this.selectedIdsValue.length > 0
  }

  // Persistence methods
  extractPagePattern(path) {
    // Extract the page pattern (e.g., "/c/123/photos", "/c/abc/assets", etc.)
    // This captures company ID + entity type, ignoring query parameters
    const match = path.match(/\/c\/[a-zA-Z0-9]+\/[a-zA-Z_]+/)
    return match ? match[0] : null
  }

  getStorageKey() {
    const currentPath = window.location.pathname
    const currentPattern = this.extractPagePattern(currentPath)
    
    // Only persist if we're on the same type of page we started on
    if (!currentPattern || currentPattern !== this.currentPagePattern) {
      // If we're on a different page type, clear existing selections for this entity
      this.clearOtherEntitySelections()
      return null // Don't persist
    }
    
    // Use the page pattern as key (ignoring query parameters)
    return `bulk_selection_${this.entityValue}_${currentPattern}`
  }

  clearOtherEntitySelections() {
    // Clear any selections for this entity type from other pages/companies
    const entityKeys = Object.keys(sessionStorage).filter(key => 
      key.startsWith(`bulk_selection_${this.entityValue}_/c/`)
    )
    entityKeys.forEach(key => {
      sessionStorage.removeItem(key)
      sessionStorage.removeItem(`${key}_timestamp`)
    })
  }

  persistSelection() {
    const storageKey = this.getStorageKey()
    if (!storageKey) return // Don't persist if not on appropriate page
    
    if (this.selectedIdsValue.length > 0) {
      sessionStorage.setItem(storageKey, JSON.stringify(this.selectedIdsValue))
    } else {
      sessionStorage.removeItem(storageKey)
    }
  }

  loadPersistedSelection() {
    const storageKey = this.getStorageKey()
    if (!storageKey) {
      this.selectedIdsValue = []
      return
    }
    
    const persisted = sessionStorage.getItem(storageKey)
    
    if (persisted) {
      try {
        this.selectedIdsValue = JSON.parse(persisted)
        this.cleanupSelection()
      } catch (e) {
        console.warn("Failed to parse persisted selection:", e)
        this.selectedIdsValue = []
        sessionStorage.removeItem(storageKey)
      }
    } else {
      this.selectedIdsValue = []
    }
  }

  restoreCheckboxStates() {
    // Restore checkbox states based on persisted selection
    this.selectedIdsValue.forEach(itemId => {
      const checkbox = this.element.querySelector(`input[data-item-id="${itemId}"]`)
      if (checkbox) {
        checkbox.checked = true
      }
    })
  }

  cleanupSelection() {
    // Don't cleanup selections when searching - keep selections even if items are not visible
    const urlParams = new URLSearchParams(window.location.search)
    
    if (urlParams.get('query')) {
      return
    }
    
    const existingIds = this.selectedIdsValue.filter(itemId => {
      return this.element.querySelector(`input[data-item-id="${itemId}"]`) !== null
    })
    
    if (existingIds.length !== this.selectedIdsValue.length) {
      this.selectedIdsValue = existingIds
      this.persistSelection()
    }
  }

  clearPersistedSelection() {
    const storageKey = this.getStorageKey()
    if (storageKey) {
      sessionStorage.removeItem(storageKey)
    }
  }

  handleFrameLoad(event) {
    if (!event?.target || !this.element.contains(event.target)) return

    this.restoreCheckboxStates()
    this.updateCounter()
    this.updateSelectAllState()
    this.toggleFloatingMenu()
  }
} 